import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import PageHeader from "@/components/ui/page-header";
import Dropdown from "@/components/ui/dropdown";
import Chip from "@/components/ui/chip";
import {
  LeftChevron,
  ArrowLeft,
  ArrowRight,
  Eye,
  Employees,
  Clock,
} from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import type { FilterField, SortOption } from "@/types/component";
import type {
  Exam,
  ExamAttendee,
  ExamAttendeeStats,
  GetExamAttendeesParams,
} from "../types/exam-management.types";
import { examManagementApi } from "../services/exam-management.api";
import styles from "./ExamAttendeesPage.module.scss";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ExamAttendeesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { examId } = useParams<{ examId: string }>();

  const [exam, setExam] = useState<Exam | null>(null);
  const [attendees, setAttendees] = useState<ExamAttendee[]>([]);
  const [stats, setStats] = useState<ExamAttendeeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string>("nameAsc");
  const [filterStatus, setFilterStatus] = useState<number | undefined>(
    undefined,
  );
  const [filterEducationId, setFilterEducationId] = useState<
    string | undefined
  >(undefined);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch exam info
  useEffect(() => {
    if (!examId) return;
    examManagementApi
      .getById(examId)
      .then(setExam)
      .catch(() => setExam(null));
  }, [examId]);

  // Map UI sort value to API params
  const getSortParams = useCallback((): {
    sortBy?: "name" | "score";
    sortOrder?: "asc" | "desc";
  } => {
    switch (sortBy) {
      case "nameAsc":
        return { sortBy: "name", sortOrder: "asc" };
      case "nameDesc":
        return { sortBy: "name", sortOrder: "desc" };
      case "scoreDesc":
        return { sortBy: "score", sortOrder: "desc" };
      case "scoreAsc":
        return { sortBy: "score", sortOrder: "asc" };
      default:
        return { sortBy: "name", sortOrder: "asc" };
    }
  }, [sortBy]);

  // Fetch attendees
  const fetchAttendees = useCallback(async () => {
    if (!examId) return;
    setIsLoading(true);
    try {
      const sortParams = getSortParams();
      const params: GetExamAttendeesParams = {
        search: debouncedSearch || undefined,
        status: filterStatus,
        ...sortParams,
      };
      const response = await examManagementApi.getAttendees(examId, params);
      setAttendees(response.data);
      setStats(response.stats);
    } catch (error) {
      console.error("Error fetching attendees:", error);
    } finally {
      setIsLoading(false);
    }
  }, [examId, debouncedSearch, sortBy, filterStatus, getSortParams]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  // Eğitim filtresi seçenekleri: sınavın ATANDIĞI eğitimler (katılımcısı olmasa da hepsi).
  const educationOptions = useMemo(
    () =>
      (exam?.relatedEducations ?? []).map((e) => ({
        value: e.id,
        label: e.name,
      })),
    [exam?.relatedEducations],
  );

  // Eğitim filtresi client-side uygulanır (backend tüm listeyi döndürüyor).
  const filteredAttendees = useMemo(
    () =>
      filterEducationId
        ? attendees.filter((a) => a.educationID === filterEducationId)
        : attendees,
    [attendees, filterEducationId],
  );

  const totalRecords = filteredAttendees.length;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalRecords / pageSize)),
    [totalRecords, pageSize],
  );

  const displayedAttendees = useMemo(
    () =>
      filteredAttendees.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [filteredAttendees, currentPage, pageSize],
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleSortChange = (option: { value?: string } | undefined) => {
    const value = option?.value ?? "nameAsc";
    setSortBy(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filters: Record<string, unknown>) => {
    const statusValue = filters.status as string | undefined;
    if (statusValue === "0" || statusValue === "1" || statusValue === "2") {
      setFilterStatus(Number(statusValue));
    } else {
      setFilterStatus(undefined);
    }
    setFilterEducationId((filters.education as string) || undefined);
    setCurrentPage(1);
  };

  const handleViewDetail = (attendee: ExamAttendee) => {
    navigate(
      `/dashboard/exam-management/${examId}/attendees/${attendee.id}`,
    );
  };

  const renderStatusChip = (attendee: ExamAttendee) => {
    switch (attendee.status) {
      case "gecti":
        return (
          <Chip type="success" size="small">
            {t("examManagement.attendees.passed")}
          </Chip>
        );
      case "kaldi":
        return (
          <Chip type="error" size="small">
            {t("examManagement.attendees.failed")}
          </Chip>
        );
      case "katilmadi":
        return (
          <Chip type="default" size="small">
            {t("examManagement.attendees.notParticipated")}
          </Chip>
        );

      case "basladi":
        return (
          <Chip type="warning" size="small">
            {t("examManagement.attendees.started")}
          </Chip>
        );
      default:
        return null;
    }
  };

  /** Backend `averageCompletionTime` saniye cinsinden */
  const formatCompletionTime = (totalSeconds: number): string => {
    if (
      totalSeconds == null ||
      Number.isNaN(totalSeconds) ||
      totalSeconds < 1
    ) {
      return "-";
    }
    const s = Math.round(totalSeconds);
    if (s < 60) {
      return t("examManagement.attendees.stats.completionSeconds", {
        count: s,
      });
    }
    const totalMinutes = Math.round(s / 60);
    if (totalMinutes < 60) {
      return t(
        "examManagement.attendees.stats.completionMinutes",
        "{{count}} dakika",
        { count: totalMinutes },
      );
    }
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return mins > 0
      ? t(
          "examManagement.attendees.stats.completionHoursMinutes",
          "{{h}} saat {{m}} dk",
          { h: hrs, m: mins },
        )
      : t("examManagement.attendees.stats.completionHours", {
          h: hrs,
        });
  };

  // Sort options
  const sortOptions: SortOption[] = [
    {
      label: t("examManagement.attendees.sort.nameAsc"),
      value: "nameAsc",
    },
    {
      label: t("examManagement.attendees.sort.nameDesc"),
      value: "nameDesc",
    },
    {
      label: t("examManagement.attendees.sort.scoreDesc"),
      value: "scoreDesc",
    },
    {
      label: t("examManagement.attendees.sort.scoreAsc"),
      value: "scoreAsc",
    },
  ];

  // Filter fields
  const filterFields: FilterField[] = [
    ...(educationOptions.length > 0
      ? [
          {
            name: "education",
            label: t("examManagement.attendees.filters.education"),
            type: "dropdown" as const,
            placeholder: t("common.all"),
            searchable: true,
            options: educationOptions,
          },
        ]
      : []),
    {
      name: "status",
      label: t("examManagement.attendees.filters.status"),
      type: "dropdown",
      placeholder: t("common.all"),
      options: [
        {
          label: t("examManagement.attendees.passed"),
          value: "1",
        },
        {
          label: t("examManagement.attendees.failed"),
          value: "2",
        },
        {
          label: t("examManagement.attendees.notParticipated"),
          value: "0",
        },
      ],
    },
  ];

  const breadcrumbItems = [
    {
      label: t("examManagement.title"),
      href: "/dashboard/exam-management",
    },
    {
      label: t("examManagement.attendees.breadcrumb"),
    },
  ];

  if (!exam && !isLoading) {
    return (
      <DashboardLayout>
        <Header title={t("examManagement.title")} />
        <div className={styles.loading}>
          {t("common.notFound")}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title={t("examManagement.title")} />
      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/dashboard/exam-management")}
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Exam Title */}
      <h2 className={styles.examTitle}>{exam?.name ?? ""}</h2>

      {/* Stats Cards */}
      {stats && (
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={styles.statLabel}>
                {t("examManagement.attendees.stats.totalParticipation")}
              </span>
              <div className={`${styles.statIcon} ${styles.statIconRed}`}>
                <Employees width={20} height={20} color="#E7212B" />
              </div>
            </div>
            <span className={styles.statValue}>
              {/*
                Liste tablosundaki t("examAttendees.participantCount") sütunu exam.participantCount
                değerini gösteriyor. Detayda stats.count'ı (attempts endpoint'i)
                ayrı kaynaktan okuyunca farklı sayılar çıkıyordu. Tutarlılık
                için aynı alandan okuyup, fallback olarak stats.count kullan.
              */}
              {exam?.participantCount ?? stats.count}
            </span>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={styles.statLabel}>
                {t("examManagement.attendees.stats.successRate")}
              </span>
              <div className={`${styles.statIcon} ${styles.statIconRed}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 2L12.09 7.26L18 8.27L14 12.14L14.81 18.02L10 15.27L5.19 18.02L6 12.14L2 8.27L7.91 7.26L10 2Z"
                    stroke="#E7212B"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <span className={styles.statValue}>%{Math.round(stats.successRate)}</span>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={styles.statLabel}>
                {t("examManagement.attendees.stats.avgCompletionTime")}
              </span>
              <div className={`${styles.statIcon} ${styles.statIconRed}`}>
                <Clock width={20} height={20} color="#E7212B" />
              </div>
            </div>
            <span className={styles.statValue}>
              {formatCompletionTime(stats.averageCompletionTime)}
            </span>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statHeader}>
              <span className={styles.statLabel}>
                {t("examManagement.attendees.stats.notParticipated")}
              </span>
              <div className={`${styles.statIcon} ${styles.statIconRed}`}>
                <Employees width={20} height={20} color="#E7212B" />
              </div>
            </div>
            <span className={styles.statValue}>{stats.notParticipatedCount}</span>
          </div>
        </div>
      )}

      <div className={styles.pageContainer}>
        <PageHeader
          searchPlaceholder={t("examManagement.attendees.searchPlaceholder")}
          searchValue={searchTerm}
          onSearch={handleSearch}
          showSort
          sortOptions={sortOptions}
          onSort={(opt) => handleSortChange(opt)}
          filters={filterFields}
          onFilterChange={handleFilterChange}
        />

        {isLoading ? (
          <div className={styles.loading}>
            {t("common.loading")}
          </div>
        ) : filteredAttendees.length === 0 ? (
          <div className={styles.emptyState}>
            {t("examManagement.attendees.noData")}
          </div>
        ) : (
          <>
            {/* Table */}
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t("examManagement.attendees.table.name")}</th>
                    <th>{t("examManagement.attendees.table.title")}</th>
                    <th>{t("examManagement.attendees.table.department")}</th>
                    <th>{t("examManagement.attendees.table.education")}</th>
                    <th>{t("examManagement.attendees.table.score")}</th>
                    <th>{t("examManagement.attendees.table.status")}</th>
                    <th>{t("examManagement.attendees.table.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedAttendees.map((attendee) => (
                    <tr key={`${attendee.id}-${attendee.educationID ?? ""}`}>
                      <td className={styles.nameCell}>{attendee.name}</td>
                      <td>{attendee.title}</td>
                      <td>{attendee.department}</td>
                      <td>{attendee.educationName}</td>
                      <td>{attendee.score !== null ? attendee.score : "-"}</td>
                      <td>{renderStatusChip(attendee)}</td>
                      <td>
                        <button
                          type="button"
                          className={styles.inspectBtn}
                          onClick={() => handleViewDetail(attendee)}
                        >
                          <Eye width={16} height={16} color="#707A8F" />
                          <span>
                            {t("examManagement.attendees.inspect")}
                          </span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles.paginationBar}>
              <div className={styles.paginationLeft}>
                <span className={styles.paginationLabel}>
                  {t("pagination.show")}
                </span>
                <div className={styles.paginationPageSizeDropdown}>
                  <Dropdown
                    onSelect={(v) =>
                      handlePageSizeChange(Array.isArray(v) ? v[0] : v)
                    }
                    options={PAGE_SIZE_OPTIONS.map((size) => ({
                      label: size.toString(),
                      value: size.toString(),
                    }))}
                    menuPosition="top"
                    selectedLabel={pageSize.toString()}
                    value={pageSize.toString()}
                  />
                </div>
                <span className={styles.paginationDetails}>
                  {t(
                    "pagination.details",
                    t("examAttendees.paginationDetails"),
                    {
                      start: (currentPage - 1) * pageSize + 1,
                      end: Math.min(currentPage * pageSize, totalRecords),
                      total: totalRecords,
                    },
                  )}
                </span>
              </div>
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ArrowLeft height={16} width={16} />
                </button>
                {renderPaginationNumbers(
                  currentPage,
                  totalPages,
                  handlePageChange,
                )}
                <button
                  type="button"
                  className={styles.paginationArrow}
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ArrowRight
                    height={16}
                    width={16}
                    color={currentPage === totalPages ? "#ccc" : "#e7212b"}
                  />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

    </DashboardLayout>
  );
};

export default ExamAttendeesPage;
